import { createSignal } from "solid-js";
import type { JSX } from "solid-js";
import type { AdminUser } from "@core/auth/session";
import { browserNotificationService } from "@core/services/BrowserNotificationService";

export type PanelDescriptor = {
  id: string;
  title: string;
  body: () => JSX.Element;
};

export type ToastMessage = {
  id: string;
  type: "success" | "error" | "warning" | "info";
  message: string;
  duration?: number;
};

export type AuthStatus = "loading" | "signed-out" | "authorizing" | "authorized" | "unauthorized";

class AppStateService {
  // ===== AUTHENTICATION =====
  private isAuthenticatedSignal = createSignal(false);
  private currentUserSignal = createSignal<AdminUser | null>(null);
  private authStatusSignal = createSignal<AuthStatus>("loading");
  private authErrorSignal = createSignal<string | null>(null);

  // ===== NAVIGATION =====
  private currentRouteSignal = createSignal("/");
  private previousRouteSignal = createSignal<string | null>(null);

  // ===== PANELS =====
  private openPanelsSignal = createSignal<PanelDescriptor[]>([]);
  private panelWidthSignal = createSignal(400);

  // ===== SELECTIONS =====
  private selectedBookingIdSignal = createSignal<string | null>(null);
  private selectedUserIdSignal = createSignal<string | null>(null);
  private selectedChatIdSignal = createSignal<string | null>(null);
  private selectedRobotIdSignal = createSignal<string | null>(null);
  private selectedLocationIdSignal = createSignal<string | null>(null);
  private selectedPaymentIdSignal = createSignal<string | null>(null);
  private selectedNotificationIdSignal = createSignal<string | null>(null);

  // ===== UI STATE =====
  private sidebarCollapsedSignal = createSignal(false);
  private showNotificationBellSignal = createSignal(true);
  private notificationCountSignal = createSignal(0);
  private isLoadingSignal = createSignal(false);
  private toastMessageSignal = createSignal<ToastMessage | null>(null);

  // ===== REAL-TIME =====
  private firestoreConnectedSignal = createSignal(false);
  private lastSyncTimestampSignal = createSignal<number | null>(null);
  private pendingChangesSignal = createSignal(0);

  // ===== GETTERS =====
  get isAuthenticated() {
    return this.isAuthenticatedSignal[0];
  }
  get currentUser() {
    return this.currentUserSignal[0];
  }
  get authStatus() {
    return this.authStatusSignal[0];
  }
  get authError() {
    return this.authErrorSignal[0];
  }

  get currentRoute() {
    return this.currentRouteSignal[0];
  }
  get previousRoute() {
    return this.previousRouteSignal[0];
  }

  get openPanels() {
    return this.openPanelsSignal[0];
  }
  get panelWidth() {
    return this.panelWidthSignal[0];
  }

  get selectedBookingId() {
    return this.selectedBookingIdSignal[0];
  }
  get selectedUserId() {
    return this.selectedUserIdSignal[0];
  }
  get selectedChatId() {
    return this.selectedChatIdSignal[0];
  }
  get selectedRobotId() {
    return this.selectedRobotIdSignal[0];
  }
  get selectedLocationId() {
    return this.selectedLocationIdSignal[0];
  }
  get selectedPaymentId() {
    return this.selectedPaymentIdSignal[0];
  }
  get selectedNotificationId() {
    return this.selectedNotificationIdSignal[0];
  }

  get sidebarCollapsed() {
    return this.sidebarCollapsedSignal[0];
  }
  get showNotificationBell() {
    return this.showNotificationBellSignal[0];
  }
  get notificationCount() {
    return this.notificationCountSignal[0];
  }
  get isLoading() {
    return this.isLoadingSignal[0];
  }
  get toastMessage() {
    return this.toastMessageSignal[0];
  }

  get firestoreConnected() {
    return this.firestoreConnectedSignal[0];
  }
  get lastSyncTimestamp() {
    return this.lastSyncTimestampSignal[0];
  }
  get pendingChanges() {
    return this.pendingChangesSignal[0];
  }

  // ===== SETTERS - AUTH =====
  setAuthenticated(value: boolean) {
    this.isAuthenticatedSignal[1](value);
  }
  setCurrentUser(user: AdminUser | null) {
    this.currentUserSignal[1](user);
  }
  setAuthStatus(status: AuthStatus) {
    this.authStatusSignal[1](status);
  }
  setAuthError(error: string | null) {
    this.authErrorSignal[1](error);
  }

  // ===== SETTERS - NAVIGATION =====
  setCurrentRoute(route: string) {
    this.previousRouteSignal[1](this.currentRoute());
    this.currentRouteSignal[1](route);
  }

  // ===== SETTERS - PANELS =====
  openPanel(panel: PanelDescriptor) {
    this.openPanelsSignal[1]((panels) => {
      if (panels.some((p) => p.id === panel.id)) return panels;
      return [...panels, panel];
    });
  }

  closePanel(panelId: string) {
    this.openPanelsSignal[1]((panels) => panels.filter((p) => p.id !== panelId));
  }

  closeAllPanels() {
    this.openPanelsSignal[1]([]);
  }

  setPanelWidth(width: number) {
    this.panelWidthSignal[1](width);
  }

  // ===== SETTERS - SELECTIONS =====
  selectBooking(id: string | null, userId?: string | null) {
    this.selectedBookingIdSignal[1](id);
    if (userId !== undefined) this.selectedUserIdSignal[1](userId);
  }

  selectUser(id: string | null) {
    this.selectedUserIdSignal[1](id);
  }

  selectChat(id: string | null, userId?: string | null) {
    this.selectedChatIdSignal[1](id);
    if (userId !== undefined) this.selectedUserIdSignal[1](userId);
  }

  selectRobot(id: string | null) {
    this.selectedRobotIdSignal[1](id);
  }

  selectLocation(id: string | null) {
    this.selectedLocationIdSignal[1](id);
  }

  selectPayment(id: string | null, userId?: string | null) {
    this.selectedPaymentIdSignal[1](id);
    if (userId !== undefined) this.selectedUserIdSignal[1](userId);
  }

  selectNotification(id: string | null) {
    this.selectedNotificationIdSignal[1](id);
  }

  clearSelections() {
    this.selectedBookingIdSignal[1](null);
    this.selectedUserIdSignal[1](null);
    this.selectedChatIdSignal[1](null);
    this.selectedRobotIdSignal[1](null);
    this.selectedLocationIdSignal[1](null);
    this.selectedPaymentIdSignal[1](null);
    this.selectedNotificationIdSignal[1](null);
  }

  // ===== SETTERS - UI =====
  setSidebarCollapsed(collapsed: boolean) {
    this.sidebarCollapsedSignal[1](collapsed);
  }
  setNotificationCount(count: number) {
    this.notificationCountSignal[1](count);
  }
  setIsLoading(loading: boolean) {
    this.isLoadingSignal[1](loading);
  }

  showToast(message: string, type: ToastMessage["type"] = "info", duration = 3000) {
    const toast: ToastMessage = {
      id: `toast-${Date.now()}`,
      type,
      message,
      duration,
    };
    this.toastMessageSignal[1](toast);

    // Auto-dismiss
    setTimeout(() => {
      if (this.toastMessage()?.id === toast.id) {
        this.toastMessageSignal[1](null);
      }
    }, duration);
  }

  dismissToast() {
    this.toastMessageSignal[1](null);
  }

  /**
   * Fire a browser notification for any event
   * Components can call this through appState
   */
  async fireNotification(title: string, body?: string, options?: { tag?: string; data?: any }): Promise<void> {
    await browserNotificationService.showNotification({
      title,
      body,
      tag: options?.tag,
      data: options?.data
    });
  }

  // ===== SETTERS - REAL-TIME =====
  setFirestoreConnected(connected: boolean) {
    this.firestoreConnectedSignal[1](connected);
    if (connected) {
      this.lastSyncTimestampSignal[1](Date.now());
    }
  }

  setPendingChanges(count: number) {
    this.pendingChangesSignal[1](count);
  }

  // ===== INITIALIZATION =====
  initialize() {
    console.log("AppState initialized");
    // Load saved UI preferences from localStorage
    const savedSidebarState = localStorage.getItem("sidebarCollapsed");
    if (savedSidebarState !== null) {
      this.setSidebarCollapsed(savedSidebarState === "true");
    }
  }

  // ===== CLEANUP =====
  cleanup() {
    // Save UI preferences to localStorage
    localStorage.setItem("sidebarCollapsed", String(this.sidebarCollapsed()));
  }
}

// Export singleton instance
export const appState = new AppStateService();
